import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { createUser } from '../../services/api';
import { formatKenyanPhone, isValidKenyanPhone } from '../../utils/phoneUtils';

const AddUserModal = ({ open, onClose, onUserCreated, companies, isSuperAdmin, isCusAdmin, currentUserCompanyId }) => {
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    phone: '+254',
    password: '',
    role: 'clerk',
    company_id: ''
  });

  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formValid, setFormValid] = useState(false);

  // Validate the form whenever formData changes
  useEffect(() => {
    const validateForm = () => {
      // Required fields check
      const requiredFieldsValid = formData.username && formData.password && formData.name && formData.role && (!isSuperAdmin || formData.company_id);
      
      // Phone validation
      const phoneValid = !formData.phone || isValidKenyanPhone(formData.phone);
      setPhoneError(phoneValid ? '' : 'Phone must be in format +254XXXXXXXXX');
      
      // Email validation (simple check)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emailValid = !formData.email || emailRegex.test(formData.email);
      setEmailError(emailValid ? '' : 'Invalid email format');
      
      setFormValid(requiredFieldsValid && phoneValid && emailValid);
    };
    
    validateForm();
  }, [formData, isSuperAdmin]);

  // Set default company for cus-admin users
  useEffect(() => {
    if (isCusAdmin && currentUserCompanyId && !formData.company_id) {
      setFormData(prev => ({
        ...prev,
        company_id: currentUserCompanyId
      }));
    }
  }, [isCusAdmin, currentUserCompanyId, formData.company_id]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        username: '',
        name: '',
        email: '',
        phone: '+254',
        password: '',
        role: 'clerk',
        company_id: isSuperAdmin && companies.length > 0 ? companies[0].id : ''
      });
      setError('');
      setPhoneError('');
      setEmailError('');
    }
  }, [open, isSuperAdmin, companies]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Format phone number when it changes
      const formattedPhone = formatKenyanPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    
    setLoading(true);
    setError('');

    try {
      const response = await createUser(formData);
      onUserCreated(response.data.user);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppDialog open={open} onClose={onClose}>
      <AppDialogTitle onClose={onClose} subtitle="Set up a new user account">
        Create New User
      </AppDialogTitle>
      <form onSubmit={handleSubmit}>
        <AppDialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            fullWidth
            required
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            required
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            required
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Email (optional)"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            error={!!emailError}
            helperText={emailError}
          />
          
          <TextField
            fullWidth
            label="Phone (optional)"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!phoneError}
            helperText={phoneError || "Format: +254XXXXXXXXX"}
            margin="normal"
            InputProps={{
              startAdornment: formData.phone && !formData.phone.startsWith('+') ? (
                <InputAdornment position="start">+</InputAdornment>
              ) : null
            }}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              name="role"
              value={formData.role}
              onChange={handleChange}
              label="Role"
              required
            >
              <MenuItem value="clerk">Clerk</MenuItem>
              {!isCusAdmin && <MenuItem value="rider">Rider</MenuItem>}
              <MenuItem value="cus-admin">Customer Admin</MenuItem>
              {isSuperAdmin && <MenuItem value="super-admin">Super Admin</MenuItem>}
            </Select>
          </FormControl>
          
          {(isSuperAdmin || isCusAdmin) && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="company-select-label">Company</InputLabel>
              <Select
                labelId="company-select-label"
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                label="Company"
                required
                disabled={isCusAdmin} // Disable for cus-admin as they can only create users in their own company
              >
                {companies.map(company => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </AppDialogContent>
        
        <AppDialogActions>
          <Button onClick={onClose} variant="outlined" color="inherit">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formValid}
          >
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        </AppDialogActions>
      </form>
    </AppDialog>
  );
};

export default AddUserModal; 